<template>
    <div>
  <div class="content">
      <div class="container-fluid mt-4">
        <stats-card
          v-if="hasOrgData"
          title="Org details" 
          type="default" 
          :sub-title="orgData.name"
          :organisation="orgData"
          icon="fas fa-sitemap"
        >
            <template slot="actions" v-if="!isNewEntry">
                <a class="dropdown-item" v-if="orgData.status !== 'deleted'" @click="showDeleteModal = true" href="javascript:void(0)">Delete Org</a>
                <a class="dropdown-item" v-if="orgData.status !== 'active'" @click="activateOrg" href="javascript:void(0)">Activate Org</a>
            </template>

            <template slot="append">
                <!-- TODO this should be a separate component -->
                <base-alert type="warning" v-if="(healthIssues.length > 0 || !!missingAccountDetailsProp.length) && !isNewEntry">
                    <template v-if="healthIssues.length > 0">
                      Please check this org's missing properties:
                      <ul>
                          <li v-for="issue in healthIssues" :key="issue">{{issue}}</li>
                      </ul>
                    </template>

                    <template v-if="!!missingAccountDetailsProp.length">
                      Please check account detail's missing properties:
                      <ul>
                          <li v-for="(missingProp, i) in missingAccountDetailsProp" :key="`missing-account-details-prop-${i}`">
                            {{ missingProp }} 
                          </li>
                      </ul>
                    </template>

                    <template v-if="(orgParts && orgParts.lureData && orgParts.lureData.pending && orgParts.lureData.pending.length === 0) && orgSensors.length > 0">
                      Please check parts management missing properties:
                      <ul>
                        <li key="missing-lure">
                          Lure type
                        </li>
                      </ul>

                    </template>
                </base-alert>

                <base-alert type="success" v-if="justCreated">
                    A default Farm &amp; Orchard have automatically been created - feel free to rename them.
                </base-alert>

                <b-card no-bod class="tab-wrapper">
                  <b-card-header header-tag="nav" v-if="!isNewEntry">
                    <b-nav card-header tabs>
                        <b-nav-item @click="setActiveTab(2)" :active="activeTab == 2">
                            Org Details
                        </b-nav-item>
                        <b-nav-item @click="setActiveTab(5)" :active="activeTab === 5">
                          Account Details
                        </b-nav-item>
                        <b-nav-item @click="setActiveTab(6)" :active="activeTab === 6">
                          Parts Management
                        </b-nav-item>
                        <b-nav-item @click="setActiveTab(1)" :active="activeTab == 1">
                          <i class="fas fa-warehouse"></i>
                            Farms
                        </b-nav-item>
                        <b-nav-item @click="setActiveTab(3)" :active="activeTab == 3">
                          <i class="fas fa-user"></i>
                            Users
                        </b-nav-item>
                        <b-nav-item @click="setActiveTab(4)" :active="activeTab == 4">
                          <i class="fas fa-map-marker-alt"></i>
                            Sensors
                        </b-nav-item>
                    </b-nav>
                  </b-card-header>
                  <div :class="{ 'tab-container': !isNewEntry }">
                    <div class="mt-3" v-if="activeTab == 1">
                      <entity-picker
                        key="farmPick"
                        @onSelect="handleFarmUpdate"
                        placeHolder="Search for a farm to add or remove"
                        apiEndpoint="getFarms"
                        :selected="orgFarms"
                        :multiple="true"
                        :canBeEmpty="true"
                        :canSelect="false"
                        :canEdit="true"
                        editPath="/farms/"
                        rootProp="farms"
                      >
                      </entity-picker>
                    </div>

                    <div class="mt-3" v-if="activeTab == 2">
                      <base-alert v-if="!isNewEntry">
                        This organisation was created on {{orgData.dateCreated}}
                      </base-alert>

                      <validation-observer ref="organisationDetailsFormRef">
                        <form-organisation 
                          :organisation="updatedOrgData"
                          :create-defaults.sync="createDefaults"
                          :is-new-entry="isNewEntry"
                        ></form-organisation>

                        <div class="mt-3" align="right">
                          <base-button type="blank" @click="handleCancelClick" v-if="isNewEntry">
                            <span>Cancel</span>
                          </base-button>

                          <base-button type="primary" @click="upsertOrganisation">
                            <span>{{ isNewEntry ? 'Create new organisation' : 'Update details' }}</span>
                          </base-button>
                        </div>
                      </validation-observer>
                    </div>

                    <div class="mt-3" v-if="activeTab == 3">
                      <entity-picker
                        key="userPick"
                        @onSelect="handleFarmUpdate"
                        placeHolder="Search for a user to add or remove"
                        apiEndpoint="getUsers"
                        :selected="orgUsers"
                        :multiple="true"
                        :canBeEmpty="true"
                        :canSelect="false"
                        :canEdit="true"
                        editPath="/users/"
                        rootProp="users"
                      >
                        </entity-picker>
                    </div>

                    <div class="mt-3" v-if="activeTab == 4">
                      <entity-picker
                        key="trapPick"
                        @onSelect="handleFarmUpdate"
                        apiEndpoint="getSensors"
                        :tableColumns="orgSensorColumns"
                        :tableOptions="orgSensorOptions"
                        :selected="orgSensors"
                        :multiple="true"
                        :canBeEmpty="true"
                        :canSelect="false"
                        :canEdit="true"
                        :canRefresh="false"
                        :canSearch="false"
                        show-count
                        editPath="/sensors/"
                        rootProp="sensors"
                      >
                      </entity-picker>
                    </div>

                    <div class="mt-3" v-if="activeTab === 5">
                      <validation-observer ref="organisationDetailsFormRef">
                        <form-account-details :account-details="updatedOrgData" />

                        <div class="mt-3" align="right">
                          <base-button type="primary" @click="upsertOrganisation">
                            Update details
                          </base-button>
                        </div>
                      </validation-observer>
                    </div>

                    <div class="mt-3" v-if="activeTab === 6">
                      <h3>Pending lures</h3>
                      <org-parts-table :columns="orgLurePendingColumns"
                                       :data="orgParts.lureData.pending"
                                       @save-edited-lure="handleUpdateLure"
                                       :editable="true"/>
                      <base-button class="m-3" type="primary" @click="showAddNewLureModal = true">Add lure</base-button>
                      <el-collapse v-model="orgLureSent" accordion>
                        <el-collapse-item name="1">
                          <template slot="title">
                            <h3 class="mt-3">Shipped lures</h3>
                          </template>
                          <org-parts-table :columns="orgLureSentColumns"
                                           :data="orgParts.lureData.sent"
                                           @save-edited-lure="handleUpdateLure"
                                           :editable="false"/>
                        </el-collapse-item>
                      </el-collapse>
                      <base-button class="m-3" type="primary" @click="showAddNewBatteryModal = true">Add battery</base-button>
                      <el-collapse v-model="orgBatterySent" accordion>
                        <el-collapse-item name="1">
                          <template slot="title">
                            <h3 class="mt-3">Shipped batteries</h3>
                          </template>
                          <org-parts-table :columns="orgBatterySentColumns"
                                           :data="orgBatteries.batteryData.sent"
                                           @save-edited-battery="handleBatteryShipment"
                                           :editable="false"/>
                        </el-collapse-item>
                      </el-collapse>
                    </div>
                  </div>
                </b-card>
            </template>
          </stats-card>
      </div>
  </div>


    <modal :show.sync="showDeleteModal">
          <h6 slot="header" class="modal-title">Delete Organisation</h6>

          <base-alert type="danger">
              Are you sure you want to <strong>delete</strong> this organisation?
          </base-alert>

<!--          <div class="form-check">-->
<!--              <input type="checkbox" v-model="createDefault" class="form-check-input" checked="checked" id="createDefault">-->
<!--              <label class="form-check-label" for="createDefault"><small>Delete children farms / orchards</small></label>-->
<!--          </div> -->

          <template slot="footer">
              <base-button type="blank" @click="showDeleteModal = false">Cancel</base-button>
              <base-button type="danger" @click="deleteOrg">Yes, delete</base-button>
          </template>
      </modal>

    <modal :show.sync="showAddNewLureModal">
      <h6 slot="header" class="modal-title">Add new lure</h6>
      <base-alert v-if="orgSensors.length === 0">
        There are no sensors associated to this organisation. Please add sensors before adding lure types.
      </base-alert>
      <form-new-org-lure v-else :lures="lures" :orgLure="orgLureNew" :orgSensors="orgSensors"/>
      <template slot="footer">
        <base-button type="blank" @click="showAddNewLureModal = false">Cancel</base-button>
        <base-button type="primary" @click="handleCreateLure" :disabled="orgSensors.length === 0">Create</base-button>
      </template>
    </modal>
    <modal :show.sync="showAddNewBatteryModal">
      <h6 slot="header" class="modal-title">Add new batteries</h6>
      <form-new-org-battery :orgBattery="orgBatteryNew" :orgSensors="orgSensors"/>
      <template slot="footer">
        <base-button type="blank" @click="showAddNewBatteryModal = false">Cancel</base-button>
        <base-button type="primary" @click="handleCreateBattery" :disabled="orgSensors.length === 0">Create</base-button>
      </template>
    </modal>
  </div>
</template>

<script>
import api from '@/api';
import { StringHelper } from '@/helpers/StringHelper';
import { TimeHelper } from '@/helpers/TimeHelper';
import { OrganisationModel } from '@/models/organisation/organisation';
import { OrganisationLureModel } from '@/models/organisation/organisationLure';
import { OrganisationBatteryModel } from '@/models/organisation/organisationBattery';
import { getEntityIssues } from '@/util/EntityHealth';
import { orgLurePendingColumns, orgLureSentColumns, orgLureOptions, orgBatterySentColumns } from '@/config/OrganisationPartsTable';
import { orgSensorColumns, orgSensorOptions } from '@/config/OrganisationSensorsTable';
import FormAccountDetails from '../components/Forms/FormAccountDetails.vue';
import FormNewOrgLure from '../components/Forms/FormNewOrgLure.vue';
import FormNewOrgBattery from '../components/Forms/FormNewOrgBattery.vue';

import moment from 'moment';

export default {
  components: {
    FormAccountDetails,
    FormNewOrgLure,
    FormNewOrgBattery
},
  data() {
    return {
      createDefaults: true,
      orgData: null,
      updatedOrgData: null,
      justCreated: false,
      activeTab: 2,
      showAddNewLureModal: false,
      showAddNewBatteryModal: false,
      showDeleteModal: false,
      showLureShipModal: false,
      lures: [],
      orgFarms: [],
      orgUsers: [],
      orgSensors: [],
      orgParts: null,
      orgBatteries: null,
      healthItems: [
          'phone',
          'email',
          'address',
      ],
      healthIssues: [],
      orgLureNew: new OrganisationLureModel(),
      orgBatteryNew: new OrganisationBatteryModel(),
      orgLurePendingColumns,
      orgLureSentColumns,
      orgLureOptions,
      orgLureSent: 1,
      orgBatterySentColumns,
      orgBatterySent: 1,
      orgSensorColumns,
      orgSensorOptions,
      accountDetails: null
    };
  },
  watch: {
    '$route.params': {
      immediate: true,
      deep: true,
      async handler(newParams, oldParams) {
        const newParamsId = !!newParams ? newParams.id : null
        const oldParamsId = !!oldParams ? oldParams.id : null

        // Check if the relevant parameter has changed
        if (newParamsId !== oldParamsId) {
          if (this.isNewEntry) {
            this.updatedOrgData = new OrganisationModel({})
            this.orgData = new OrganisationModel({})
          } else {
            this.$emit('loading', true);
            const results = await api.getOrg(this.$route.params.id)
            const lureResults = await api.getLures();
            const sensorResults = await api.getOrgSensors(this.$route.params.id);
            await this.fetchOrgBatteries();
            await this.fetchOrgParts();
            this.lures = lureResults.records;
            this.orgSensors = sensorResults.sensors;
            
            this.$emit('loading', false);
            this.justCreated = this.$route.query.created !== undefined
            this.orgData = new OrganisationModel(results)

            this.updatedOrgData = new OrganisationModel(results)
            this.checkEntityHealth();
          }
        }
      },
    },
  },
  created: function() {
    // NOTE: since we are redirecting to the same page with different params (on new orgnisation i.e. from /new to /{id})
    // and since created / mounted lifecycle only gets triggered once
    // we need to move fetching the organisation details inside a watcher to do an actual fetch
  },
  computed: {
    isNewEntry() {
      return this.$route.params.id.toLowerCase() === 'new'
    },
    missingAccountDetailsProp() {
      const { accountManager, contractStart, contractEnd, contractTermMonths } = this.orgData

      const props = {
        accountManager: !Object.keys(accountManager).length,
        contractTermMonths: !Number(contractTermMonths),
        contractDates: !contractStart || !contractEnd
      }

      return Object.keys(props).filter(f => props[f])
    },

    hasOrgData() {
      return !!Object.keys(this.orgData || {}).length
    }
  },
  methods: {
    handleCancelClick() {
      this.$router.push({ path: '/organisations' })
    },
    async createNewOrg() {
      this.$emit('loading', true);
      const results = await api.createOrg(this.updatedOrgData.exportToCreate({ defaults: { create: this.createDefaults } }));
      this.$emit('loading', false);

      if (results) {
        this.$router.push(`/organisations/${results.id}${results && results.farm && results.orchard ? '?created' : ''}`);
        this.$emit('onApiResult', 'Org created, redirecting you to it...')
      }
    },
    async upsertOrganisation() {
      const isValidForm = await this.$refs.organisationDetailsFormRef.validate()

      if (!isValidForm) return false

      this.updatedOrgData = StringHelper.trimObjectStrings(this.updatedOrgData);

      if (this.isNewEntry) {
        await this.createNewOrg()
      } else {
        await this.updateOrg()
      }
    },
    setActiveTab: async function(tab) {
      if (tab === 1) {
        this.$emit('loading', true);
        const results = await api.getOrgFarms(this.$route.params.id);
        this.$emit('loading', false);
        this.orgFarms = results.farms;
      }
      if (tab === 3) {
        this.$emit('loading', true);
        const results = await api.getOrgUsers(this.$route.params.id);
        this.$emit('loading', false);
        this.orgUsers = results.users;
      }
      if (tab === 4) {
        this.$emit('loading', true);
        const results = await api.getOrgSensors(this.$route.params.id);
        this.$emit('loading', false);
        this.orgSensors = results.sensors;
      }
      if (tab === 6) {
        this.$emit('loading', true);
        await this.fetchOrgBatteries();
        await this.fetchOrgParts();
        this.$emit('loading', false);
      }
      this.activeTab = tab;
    },
    fetchOrgBatteries: async function() {
      const results = await api.getOrgBatteries(this.$route.params.id);
      if(results && results.batteryData) {
        const sent = results.batteryData.sent || [];
        const batteries = {
          batteryData: {
            sent: sent.map(sentBattery => new OrganisationBatteryModel(sentBattery))
          }
        };
        this.orgBatteries = batteries;
      }
    },
    fetchOrgParts: async function() {
      const orgResults = await api.getOrgParts(this.$route.params.id);
      const pending = orgResults.lureData.pending || [];
      const sent = orgResults.lureData.sent || [];
      const orgParts = {
        lureData: {
          pending: pending.map(pendingLure => new OrganisationLureModel(pendingLure)),
          sent: sent.map(sentLure => new OrganisationLureModel(sentLure))
        }
      };
      this.orgParts = orgParts;
    },
    getLureByCode: function(productCode) {
      for (const type of this.lures) {
        const match = type.lures.find(lure => lure.productCode === productCode);
        if (match) {
          return match;
        }
      }
    },
    handleCreateLure: async function() {
      this.$emit('loading', true);
      await api.createOrgLure(this.$route.params.id, this.orgLureNew);
      await this.fetchOrgBatteries();
      await this.fetchOrgParts();
      this.showAddNewLureModal = false;
      this.orgLureNew = new OrganisationLureModel();
      this.$emit('loading', false);
      this.$emit('onApiResult', "Lure has been added");
    },
    handleCreateBattery: async function() {
      this.$emit('loading', true);
      await api.createOrgBattery(this.$route.params.id, this.orgBatteryNew);
      await this.fetchOrgBatteries();
      await this.fetchOrgParts();
      this.showAddNewBatteryModal = false;
      this.orgBatteryNew = new OrganisationBatteryModel();
      this.$emit('loading', false);
      this.$emit('onApiResult', "Battery shipment has been added");
    },
    handleUpdateLure: async function(payload) {
      this.$emit('loading', true);
      if(payload.batteryQuantity && payload.batteryQuantity > 0) {
        await this.handleBatteryShipment(payload);
      }
      else {
        await api.updateOrgParts(this.$route.params.id, payload.lure);
        if (payload.createNew) {
          const newPendingLure = new OrganisationLureModel({...payload.lure})
          newPendingLure.orgLureId = null;
          const lureType = this.getLureByCode(newPendingLure.productCode)
          const momentDueDate = moment(newPendingLure.lureShipDate);
          momentDueDate.add(lureType.rate, lureType.frequency);
          newPendingLure.lureDueDate = momentDueDate.format("YYYY-MM-DD");
          newPendingLure.lureShipDate = null;
          await api.createOrgLure(this.$route.params.id, newPendingLure);
        }
      }
      await this.fetchOrgBatteries();
      await this.fetchOrgParts();

      this.$emit('loading', false);
      this.$emit('onApiResult', "Parts has been updated");
    },
    handleBatteryShipment: async function(payload) {
      await api.createOrgBattery(this.$route.params.id, payload);
    },
    handleFarmUpdate: function(farms) {
        this.updatedOrgData.farms = farms;

        // TODO: add address validation before saving

        this.updateOrg();
    },
    updateOrg: async function() {
      this.$emit('loading', true);

      const payload = this.updatedOrgData.toDetailsUpdateJson(this.orgData);
      
      if (payload.contractStart) {
        payload.contractStart = TimeHelper.toSeconds(payload.contractStart)
      }

      if (payload.contractEnd) {
        payload.contractEnd = TimeHelper.toSeconds(payload.contractEnd)
      }

      await api.updateOrg(this.orgData.id, payload);

      this.$emit('loading', false);
      this.$emit('onApiResult', "Org has been updated");
    },
    deleteOrg: async function() {
        this.showDeleteModal = false
        this.updatedOrgData.status = "deleted"
        await this.updateOrg();
        this.$router.push(`/organisations`);
    },
    activateOrg: async function() {
        this.updatedOrgData.status = "active"
        await this.updateOrg();
        this.$emit('loading', true);
        const results = await api.getOrg(this.$route.params.id)
        this.$emit('loading', false);
        this.justCreated = this.$route.query.created !== undefined
        this.orgData = new OrganisationModel(results)
        this.updatedOrgData = new OrganisationModel(results)

    },
    checkEntityHealth: function() {
        this.healthIssues = getEntityIssues(this.healthItems,this.orgData)
    }
  }
};
</script>
